import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { trackEvent } from '../../../client/analytics';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

import { DEFAULT_ENVELOPE_BLOCK, TRIGGER_LOCATIONS, TRIGGER_TYPE } from '../../constants/ClickstreamConstants';

const EVENT_IDS = {
  RENTERS_INSURANCE_VIEW_CONTENT: '5312',
  RENTERS_INSURANCE_CLICK_YES: '5309',
  RENTERS_INSURANCE_CLICK_NO: '5310',
  RENTERS_INSURANCE_CLICK_CLEAR: '5311',
  RENTERS_INSURANCE_INFO_POPOVER: '6146',
} as const;

const RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS = {
  POSTING_PATH: TRIGGER_LOCATIONS.POSTING_PATH,
  POSTING_PATH_REVIEW: TRIGGER_LOCATIONS.POSTING_PATH_REVIEW,
};

// https://dataportal.zillowgroup.net/event-registry/5312
const RentersInsuranceViewContent = ({
  currentProperty,
  user,
  triggerLocation = RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS.POSTING_PATH,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation?: (typeof RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS)[keyof typeof RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.RENTERS_INSURANCE_VIEW_CONTENT,
      ...DEFAULT_ENVELOPE_BLOCK,
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.IMPRESSION,
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'posting_path',
      trigger_object_nm: 'renters_insurance_question',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/5309
const RentersInsuranceClickYes = ({
  currentProperty,
  user,
  triggerLocation = RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS.POSTING_PATH,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation?: (typeof RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS)[keyof typeof RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.RENTERS_INSURANCE_CLICK_YES,
      ...DEFAULT_ENVELOPE_BLOCK,
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'renters_insurance_required_yes',
      trigger_object_nm: 'no_trigger_object',
    },
    semantic: {
      semantic_event_nm: 'create_listing',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/5310
const RentersInsuranceClickNo = ({
  currentProperty,
  user,
  triggerLocation = RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS.POSTING_PATH,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation?: (typeof RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS)[keyof typeof RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.RENTERS_INSURANCE_CLICK_NO,
      ...DEFAULT_ENVELOPE_BLOCK,
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'renters_insurance_required_no',
      trigger_object_nm: 'no_trigger_object',
    },
    semantic: {
      semantic_event_nm: 'create_listing',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/5311
const RentersInsuranceClickClear = ({
  currentProperty,
  user,
  triggerLocation = RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS.POSTING_PATH,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation?: (typeof RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS)[keyof typeof RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.RENTERS_INSURANCE_CLICK_CLEAR,
      ...DEFAULT_ENVELOPE_BLOCK,
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'renters_insurance_required_clear',
      trigger_object_nm: 'no_trigger_object',
    },
    semantic: {
      semantic_event_nm: 'create_listing',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6146
const RentersInsuranceInfoPopover = ({
  currentProperty,
  user,
  triggerLocation = RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS.POSTING_PATH,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation?: (typeof RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS)[keyof typeof RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS];
}) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.RENTERS_INSURANCE_INFO_POPOVER,
      ...DEFAULT_ENVELOPE_BLOCK,
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.INTERACTION,
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'help_tooltip',
      trigger_object_nm: 'renters_insurance_question',
    },
    semantic: {
      semantic_event_nm: 'view_tooltip',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

const RentersInsuranceCSEvents = {
  RentersInsuranceClickClear,
  RentersInsuranceClickNo,
  RentersInsuranceClickYes,
  RentersInsuranceViewContent,
  RentersInsuranceInfoPopover,
};

// Clickstream visualizer data-eventid attributes
RentersInsuranceClickClear.data_event_id = EVENT_IDS.RENTERS_INSURANCE_CLICK_CLEAR;
RentersInsuranceClickNo.data_event_id = EVENT_IDS.RENTERS_INSURANCE_CLICK_NO;
RentersInsuranceClickYes.data_event_id = EVENT_IDS.RENTERS_INSURANCE_CLICK_YES;
RentersInsuranceViewContent.data_event_id = EVENT_IDS.RENTERS_INSURANCE_VIEW_CONTENT;
RentersInsuranceInfoPopover.data_event_id = EVENT_IDS.RENTERS_INSURANCE_INFO_POPOVER;

export { RENTERS_INSURANCE_CS_TRIGGER_LOCATIONS };

export default RentersInsuranceCSEvents;
